import { useState, useEffect, FormEvent } from "react";
import DashboardLayout from "@/layouts/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationContext";
import api from "@/services/api";

export default function DashboardProfile() {
  const { user, updateUser } = useAuth();
  const { showToast } = useNotifications();

  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    company: user?.company || "",
    phone: user?.phone || (user as any)?.phone || "",
    job_title: user?.job_title || (user as any)?.jobTitle || "",
    avatar_url: user?.avatarUrl || (user as any)?.avatarUrl || "",
  });

  const [passwords, setPasswords] = useState({ current: "", newPass: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const [avatarError, setAvatarError] = useState(false);

  // Fetch fresh user data on mount to ensure latest values
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await api.get("/auth/me");
        const freshUser = res.data.data;
        setForm({
          name: freshUser.name || "",
          email: freshUser.email || "",
          company: freshUser.company || "",
          phone: freshUser.phone || "",
          job_title: freshUser.jobTitle || freshUser.job_title || "",
          avatar_url: freshUser.avatarUrl || "",
        });
        setAvatarError(false); // reset error when new avatar loads
      } catch (err) {
        // Fallback to existing user context
        if (user) {
          setForm({
            name: user.name || "",
            email: user.email || "",
            company: user.company || "",
            phone: user.phone || (user as any)?.phone || "",
            job_title: user.job_title || (user as any)?.jobTitle || "",
            avatar_url: user.avatarUrl || (user as any)?.avatarUrl || "",
          });
        }
      }
    };
    fetchProfile();
  }, []);

  const handleProfile = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload: any = {
        full_name: form.name,
        company: form.company || undefined,
        phone: form.phone || undefined,
        job_title: form.job_title || undefined,
        avatar_url: form.avatar_url || undefined,
      };

      const res = await api.patch("/users/profile", payload);
      const updated = res.data.data;

      // Update local auth state
      updateUser({
        name: updated.full_name,
        company: updated.company,
        phone: updated.phone,
        job_title: updated.job_title,
        avatarUrl: updated.avatar_url,
      });

      showToast("success", "Profile updated successfully.");
    } catch (err: any) {
      showToast("error", err.response?.data?.message || "Failed to update profile.");
    } finally {
      setSaving(false);
    }
  };

  const handlePassword = async (e: FormEvent) => {
    e.preventDefault();
    if (passwords.newPass !== passwords.confirm) {
      showToast("error", "Passwords do not match.");
      return;
    }
    if (passwords.newPass.length < 6) {
      showToast("error", "Password must be at least 6 characters.");
      return;
    }
    setSaving(true);
    try {
      await api.patch("/users/profile", {
        currentPassword: passwords.current,
        newPassword: passwords.newPass,
      });
      showToast("success", "Password updated successfully.");
      setPasswords({ current: "", newPass: "", confirm: "" });
    } catch (err: any) {
      showToast("error", err.response?.data?.message || "Failed to update password.");
    } finally {
      setSaving(false);
    }
  };

  const initials = user?.name?.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) || "U";

  // Convert Google Drive links to thumbnail URLs (same as sidebar)
  const convertDriveLink = (url: string) => {
    if (!url) return "";
    if (url.includes("drive.google.com")) {
      const fileMatch = url.match(/\/file\/d\/([^\/]+)/);
      if (fileMatch) {
        return `https://drive.google.com/thumbnail?id=${fileMatch[1]}&sz=w200`;
      }
      const idMatch = url.match(/[?&]id=([^&]+)/);
      if (idMatch) {
        return `https://drive.google.com/thumbnail?id=${idMatch[1]}&sz=w200`;
      }
    }
    return url;
  };

  const displayAvatarUrl = convertDriveLink(form.avatar_url);

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-2xl">
        {/* Header with Avatar */}
        <div className="flex items-center gap-5">
          {displayAvatarUrl && !avatarError ? (
            <img
              src={displayAvatarUrl}
              alt="Avatar"
              className="w-20 h-20 rounded-full object-cover border-2 border-electric-blue/30 flex-shrink-0"
              referrerPolicy="no-referrer"
              onError={() => setAvatarError(true)}
            />
          ) : (
            <div className="w-20 h-20 flex items-center justify-center rounded-full bg-electric-blue/20 border-2 border-electric-blue/30 text-electric-blue text-3xl font-black flex-shrink-0">
              {initials}
            </div>
          )}
          <div>
            <h1 className="text-2xl font-black text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Profile Settings</h1>
            <p className="text-silver text-sm mt-1">Manage your account information.</p>
            <div className="mt-2">
              <div className="text-white font-bold text-lg">{user?.name}</div>
              <div className="text-silver-dim text-sm">{user?.email}</div>
              <span className="inline-block mt-1 text-xs px-2.5 py-0.5 rounded-full bg-electric-blue/10 border border-electric-blue/30 text-electric-blue capitalize">{user?.role}</span>
            </div>
          </div>
        </div>

        {/* Profile Form */}
        <div className="bg-dark-card border border-dark-border rounded-2xl p-6">
          <h2 className="text-white font-bold text-base mb-5" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Personal Information</h2>
          <form onSubmit={handleProfile} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-silver text-xs font-medium mb-2 uppercase tracking-wider">Full Name</label>
                <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required
                  className="w-full bg-deep-black border border-dark-border rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors" />
              </div>
              <div>
                <label className="block text-silver text-xs font-medium mb-2 uppercase tracking-wider">Job Title</label>
                <input type="text" value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })}
                  className="w-full bg-deep-black border border-dark-border rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                  placeholder="e.g. Senior Developer" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-silver text-xs font-medium mb-2 uppercase tracking-wider">Phone</label>
                <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full bg-deep-black border border-dark-border rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                  placeholder="+1 (555) 123-4567" />
              </div>
              <div>
                <label className="block text-silver text-xs font-medium mb-2 uppercase tracking-wider">Company</label>
                <input type="text" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
                  className="w-full bg-deep-black border border-dark-border rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors" />
              </div>
            </div>

            <div>
              <label className="block text-silver text-xs font-medium mb-2 uppercase tracking-wider">Avatar URL</label>
              <input 
                type="url" 
                value={form.avatar_url} 
                onChange={(e) => {
                  setForm({ ...form, avatar_url: e.target.value });
                  setAvatarError(false); // reset error when user types new URL
                }}
                className="w-full bg-deep-black border border-dark-border rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                placeholder="https://example.com/avatar.jpg" />
              <p className="text-silver-dim text-xs mt-1">Enter a direct image URL for your avatar.</p>
            </div>

            <div>
              <label className="block text-silver text-xs font-medium mb-2 uppercase tracking-wider">Email Address</label>
              <input type="email" value={form.email} disabled
                className="w-full bg-deep-black border border-dark-border rounded-xl px-4 py-3 text-silver-dim text-sm cursor-not-allowed opacity-60" />
              <p className="text-silver-dim text-xs mt-1">Email cannot be changed. Contact support if needed.</p>
            </div>

            <button type="submit" disabled={saving}
              className="bg-electric-blue text-deep-black font-semibold px-6 py-2.5 rounded-xl text-sm hover:shadow-glow-blue transition-all cursor-pointer whitespace-nowrap disabled:opacity-50">
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </form>
        </div>

        {/* Password Form */}
        <div className="bg-dark-card border border-dark-border rounded-2xl p-6">
          <h2 className="text-white font-bold text-base mb-5" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Change Password</h2>
          <form onSubmit={handlePassword} className="space-y-4">
            {[
              { label: "Current Password", key: "current" as const },
              { label: "New Password", key: "newPass" as const },
              { label: "Confirm New Password", key: "confirm" as const },
            ].map((field) => (
              <div key={field.key}>
                <label className="block text-silver text-xs font-medium mb-2 uppercase tracking-wider">{field.label}</label>
                <input type="password" value={passwords[field.key]} onChange={(e) => setPasswords({ ...passwords, [field.key]: e.target.value })} required
                  className="w-full bg-deep-black border border-dark-border rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors" />
              </div>
            ))}
            <button type="submit" disabled={saving}
              className="bg-electric-blue text-deep-black font-semibold px-6 py-2.5 rounded-xl text-sm hover:shadow-glow-blue transition-all cursor-pointer whitespace-nowrap disabled:opacity-50">
              {saving ? "Updating..." : "Update Password"}
            </button>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
}
